import React from 'react';
import moment from 'moment';
import Highcharts from 'highcharts';
import colors from 'src/utils/colors';
import _isEmpty from 'lodash/isEmpty';
import numeral from 'numeral';
import { Option } from 'funfix-core';
import ReactDOMServer from 'react-dom/server.browser';

export const StackedColumnTooltip: React.FC<{
  datas: any[];
  time: any;
  dailyBudgets: [number, number][];
}> = ({ datas, time, dailyBudgets }) => {
  const date = moment(time).format('MMM D').toUpperCase();
  const tooltipStyle = {
    container: {
      color: colors.grey,
      fontSize: 10,
      padding: '12px 32px 12px 12px'
    },
    title: { fontSize: 14, color: colors.grey }
  };

  // Sort datas to ensure "actual spend" is always first
  const sortedDatas = [...datas].sort((a, b) => {
    if (a.series.name.toLowerCase() === 'actual spend') {
      return -1;
    }
    if (b.series.name.toLowerCase() === 'actual spend') {
      return 1;
    }
    return 0;
  });

  return (
    <div style={tooltipStyle.container} className="hichart-tooltip">
      <div style={tooltipStyle.title}>{date}</div>
      {sortedDatas.map((data, idx) => {
        if (!data.point.percentage || _isEmpty(data.series.name)) {
          return null;
        }

        const { amount, percent }: { amount: number; percent: number } = (() => {
          const dailyBudgetForTheDay = dailyBudgets[data.point.index][1];
          if (['actual spend', 'projected spend'].includes(data.series.name.toLowerCase())) {
            const overpacingAmount = Option.of(datas.find((datum) => datum.series.name.toLowerCase() === 'overpacing'))
              .map((datum) => datum.point.y)
              .getOrElse(null);

            if (overpacingAmount !== null) {
              return {
                percent: (data.point.y + overpacingAmount) / dailyBudgetForTheDay,
                amount: data.point.y + overpacingAmount
              };
            }
          }

          return {
            percent: data.point.y / dailyBudgetForTheDay,
            amount: data.point.y
          };
        })();

        const displayValue = ` ${numeral(amount).format('$1,000')}`;
        const color = data.series.color === '#EAECF1' ? colors.labelGrey : data.series.color;

        return (
          <div
            key={idx}
            style={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                textTransform: 'uppercase',
                fontSize: 10,
                fontWeight: 700,
                marginBottom: 4,
                display: 'flex',
                alignItems: 'center',
                color: colors.grey,
                letterSpacing: '0.96px'
              }}
            >
              <div
                style={{
                  borderRadius: '100%',
                  width: 8,
                  height: 8,
                  backgroundColor: color,
                  marginRight: 8
                }}
              />
              {data.series.name}
            </div>
            <span
              style={{
                fontSize: 14,
                color: colors.darkBlue,
                fontWeight: 500,
                letterSpacing: '0.08px',
                marginLeft: 16
              }}
            >
              {displayValue}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500
                }}
              >
                {` (${numeral(percent).format('100%')})`}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const getChartOptions = ({ dailyBudgets }: { dailyBudgets: [number, number][] }) => {
  return {
    chart: {
      height: 244,
      type: 'column',
      marginLeft: 0,
      marginRight: 0,
      width: 1040,
      style: {
        margin: 0,
        padding: 0
      },
      spacing: [0, 0, 0, 0]
    },
    title: {
      text: ''
    },
    legend: {
      enabled: false
    },
    xAxis: {
      crosshair: false,
      type: 'datetime',
      tickLength: 0,
      tickWidth: 0,
      lineWidth: 0,
      dateTimeLabelFormats: {
        day: '%e'
      },
      min: dailyBudgets[0][0],
      max: dailyBudgets[dailyBudgets.length - 1][0],
      tickPositioner() {
        const positions = [];
        for (let i = 0; i < dailyBudgets.length; i++) {
          positions.push(dailyBudgets[i][0]);
        }
        return positions;
      },
      labels: {
        formatter() {
          return Highcharts.dateFormat('%e', this.value); // Formats as day of the month (1, 2, 3, etc.)
        },
        style: {
          fontFamily: 'Roboto, sans-serif',
          marginTop: '16px',
          color: colors.darkBlue,
          fontSize: '11px'
        }
      }
    },
    yAxis: {
      visible: false,
      min: 0,
      title: {
        text: ''
      }
    },
    credits: {
      enabled: false
    },
    exporting: { enabled: false },
    tooltip: {
      borderWidth: 1,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      useHTML: true,
      shadow: false,
      shared: true,
      positioner(labelWidth: number, labelHeight: number, point: any) {
        return { x: point.plotX, y: point.plotY - 20 };
      },
      formatter() {
        const inst = this as any;
        return ReactDOMServer.renderToString(
          <StackedColumnTooltip datas={inst.points} time={inst.x} dailyBudgets={dailyBudgets} />
        );
      }
    },
    plotOptions: {
      column: {
        stacking: 'percent',
        borderWidth: 0,
        groupPadding: 0,
        pointPadding: 0,
        pointWidth: 20
      }
    }
  };
};
